import { router } from '@inertiajs/react';
import { Folder, Clock, FileText, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { SearchResult, RecentSearch } from '../types/search.types';
import { highlightText } from '../utils/highlightText';
import { formatBytes } from '@utils/formatBytes';
import MimeTypeIcon from '@features/file-explorer/components/Table/MimeTypeBadge';
import UserAvatar from '@components/UserAvatar';

interface SearchResultsProps {
  results: SearchResult[];
  query: string;
  recentSearches: RecentSearch[];
  onClose: () => void;
  onRecentSearchClick: (query: string) => void;
}

export default function SearchResults({
  results,
  query,
  recentSearches,
  onClose,
  onRecentSearchClick,
}: SearchResultsProps) {
  const { t } = useTranslation();

  const handleResultClick = (result: SearchResult) => {
    if (result.is_folder) {
      router.get(`/${result.id}`);
    } else {
      router.get(`/documents/${result.id}/preview`);
    }
    onClose();
  };

  const handleKeyDown = (e: React.KeyboardEvent, result: SearchResult) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleResultClick(result);
    }
  };

  const handleRecentSearchKeyDown = (e: React.KeyboardEvent, searchQuery: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onRecentSearchClick(searchQuery);
    }
  };

  // Show recent searches if no query
  if (!query || query.trim().length < 2) {
    if (recentSearches.length === 0) {
      return (
        <div className="p-4 text-center text-sm text-base-content/60">
          {t('search.start_typing')}
        </div>
      );
    }

    return (
      <div className="p-2">
        <div className="px-2 py-1 text-xs font-semibold text-base-content/60 uppercase">
          {t('search.recent_searches')}
        </div>
        <ul className="menu menu-sm p-0">
          {recentSearches.map((recent) => (
            <li key={recent.timestamp}>
              <button
                onClick={() => onRecentSearchClick(recent.query)}
                onKeyDown={(e) => handleRecentSearchKeyDown(e, recent.query)}
                className="flex items-center gap-2"
              >
                <Clock className="w-4 h-4 text-base-content/40" />
                <span className="flex-1 text-left">{recent.query}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  // No results
  if (results.length === 0) {
    return (
      <div className="p-8 text-center">
        <div className="text-base-content/40 mb-2">
          <svg
            className="w-16 h-16 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <p className="text-sm font-medium text-base-content/80">
          {t('search.no_results')}
        </p>
        <p className="text-xs text-base-content/60 mt-1">
          {t('search.try_different_keywords')}
        </p>
      </div>
    );
  }

  return (
    <div className="max-h-[60vh] overflow-y-auto">
      <div className="p-2">
        <div className="px-2 py-1 text-xs font-semibold text-base-content/60 uppercase">
          {t('search.results', { count: results.length })} ({results.length})
        </div>
        <ul className="w-full space-y-0.5 p-0">
          {results.map((item) => (
            <li key={`${item.type}-${item.id}`}>
              <button
                onClick={() => handleResultClick(item)}
                onKeyDown={(e) => handleKeyDown(e, item)}
                className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-base-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 cursor-pointer"
              >
                {/* Content: 2 rows */}
                <div className="space-y-1">
                  {/* Row 1: Icon Name Type File size Avatar */}
                  <div className="flex items-center gap-2">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {item.is_folder ? (
                        <Folder className="w-4 h-4 text-primary" />
                      ) : item.mime_type ? (
                        <MimeTypeIcon mimeType={item.mime_type} extension={item.extension} />
                      ) : (
                        <FileText className="w-4 h-4 text-base-content/70" />
                      )}
                    </div>

                    <span className="font-medium text-sm">
                      {highlightText(item.name, query)}
                    </span>

                    {/* Type (extension) */}
                    {!item.is_folder && item.extension && (
                      <span className="text-xs font-medium text-base-content/50">
                        {item.extension.toUpperCase()}
                      </span>
                    )}

                    {/* File size */}
                    {!item.is_folder && item.size && (
                      <span className="text-xs text-base-content/50">
                        {formatBytes(item.size)}
                      </span>
                    )}

                    {/* Owner avatar */}
                    {item.owner && (
                      <div className="flex-shrink-0">
                        <UserAvatar user={item.owner} size="xs" showTooltip={true} />
                      </div>
                    )}
                  </div>

                  {/* Row 2: Path */}
                  <div className="flex items-center gap-2 text-xs text-base-content/40">

                    {/* Path */}
                    {item.breadcrumb_path && item.breadcrumb_path.length > 0 && (
                      <div className="flex items-center gap-0.5 truncate">
                        {(item.is_folder
                          ? item.breadcrumb_path.slice(0, -1)
                          : item.breadcrumb_path
                        ).map((pathItem, index, array) => (
                          <span key={pathItem.id} className="flex items-center gap-0.5">
                            <span className="truncate">{pathItem.name}</span>
                            {index < array.length - 1 && (
                              <ChevronRight className="w-3 h-3 flex-shrink-0" />
                            )}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
